import type { TFiltersContextOptions } from "@amphore-dev/amphore-lib";

import { useFiltersContext } from "@/hooks/useFiltersContext";

import { DEFAULT_PROJECTS_FILTERS } from "@/constants/CProjects";

import { TProjectsFilters } from "@/types";

/** "3,0" ↔ [3, 0]; anything that is not a non-negative integer is dropped. */
const parseIds = (raw: string): number[] =>
	raw
		.split(",")
		.filter((part) => part.trim() !== "")
		.map(Number)
		.filter((id) => Number.isInteger(id) && id >= 0);

/**
 * The /projects page's filters follow the URL (`?search=&clients=3,0
 * &display=list&archived=1`): read once on mount, written on every
 * change. Defaults write nothing, so a bare /projects stays bare. Only
 * the projects page may call this hook: the options are per call in the
 * lib, and a consumer elsewhere would write these params onto its own URL.
 */
export const PROJECTS_FILTERS_OPTIONS: Partial<
	TFiltersContextOptions<TProjectsFilters>
> = {
	updateUrlParams: true,
	resetPageOnChange: false,
	// The provider keeps every slice in sessionStorage; explicit defaults
	// win over that on mount, so the URL alone says what is filtered: a
	// param left out is its default, never the previous visit's value.
	defaultValues: DEFAULT_PROJECTS_FILTERS,
	// The badge on the "Filters" button: the client choice and the archived
	// switch; the search has its own field.
	countCallback: (filters) =>
		(filters.clientIds?.length ? 1 : 0) + (filters.archived ? 1 : 0),
	params: {
		search: true,
		clientIds: {
			key: "clients",
			// ActiveFilters clears a list's last chip to null, not [].
			get: (ids) => (ids?.length ? ids.join(",") : undefined),
			set: parseIds,
		},
		display: {
			get: (display) => ("list" === display ? "list" : undefined),
			set: (raw) => ("list" === raw ? "list" : "grouped"),
		},
		archived: {
			get: (archived) => (archived ? "1" : undefined),
			set: (raw) => "1" === raw,
		},
	},
};

export const useProjectsFilters = () =>
	useFiltersContext("projects", PROJECTS_FILTERS_OPTIONS);
